import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import Img from "@/components/Img";
import { JOURNAL } from "@/data/content";

export default function JournalPost() {
  const { slug } = useParams();
  const post = JOURNAL.posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="mx-auto max-w-[900px] px-6 md:px-10 py-32 md:py-40" data-testid="journal-post-not-found">
        <p className="overline text-sage mb-4">{JOURNAL.eyebrow}</p>
        <h1 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight tracking-tight">
          This story hasn't been written yet.
        </h1>
        <Link
          to="/journal"
          className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase text-ink hover:text-sage transition-colors"
          data-testid="journal-post-back"
        >
          <ArrowLeft size={14} /> Back to the journal
        </Link>
      </div>
    );
  }

  const nextPost = (() => {
    const idx = JOURNAL.posts.findIndex((p) => p.slug === slug);
    return JOURNAL.posts[(idx + 1) % JOURNAL.posts.length];
  })();

  return (
    <article className="pt-8 md:pt-12">
      <section className="mx-auto max-w-[900px] px-6 md:px-10">
        <Link
          to="/journal"
          className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-inksoft hover:text-ink transition-colors mb-10"
          data-testid="journal-post-back"
        >
          <ArrowLeft size={13} /> The journal
        </Link>

        <div className="flex items-center gap-3 text-xs tracking-[0.16em] uppercase text-inksoft mb-6">
          <span>{post.date}</span>
          <span className="h-px w-6 bg-ink/20" />
          <span className="text-sage">{post.category}</span>
        </div>

        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          lines={[post.title]}
        />

        {post.excerpt && (
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg md:text-xl text-inksoft leading-relaxed font-serif italic max-w-2xl">
              {post.excerpt}
            </p>
          </Reveal>
        )}
      </section>

      <section className="mx-auto max-w-[900px] px-6 md:px-10 mt-14 md:mt-20">
        <Reveal>
          <div className="bg-stone2 overflow-hidden mx-auto w-fit max-w-full">
            <img
              src={post.hero}
              alt={post.title}
              loading="lazy"
              className="block mx-auto w-auto h-auto max-w-full max-h-[560px]"
            />
          </div>
        </Reveal>
      </section>

      {post.story && post.story.length > 0 && (
        <section className="mx-auto max-w-[720px] px-6 md:px-10 mt-16 md:mt-24">
          <div className="space-y-6 text-inksoft leading-[1.85] text-[17px]">
            {post.story.map((p, i) => {
              const isHeading = typeof p === "string" && p.startsWith("## ");
              return (
                <Reveal key={i} delay={i * 0.03}>
                  {isHeading ? (
                    <h3 className="font-serif font-light text-ink text-2xl md:text-3xl leading-tight tracking-tight pt-6 md:pt-10">
                      {p.replace(/^##\s+/, "")}
                    </h3>
                  ) : (
                    <p>{p}</p>
                  )}
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {post.gallery && post.gallery.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-20 md:mt-28">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
            {post.gallery.map((src, i) => (
              <Reveal key={src + i} delay={(i % 6) * 0.04}>
                <div className="mb-4 md:mb-6 break-inside-avoid">
                  <Img src={src} alt={`${post.title} — ${i + 1}`} natural />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[900px] px-6 md:px-10 mt-24 md:mt-32 pb-32">
        <div className="border-t border-ink/10 pt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-inksoft hover:text-ink transition-colors"
            data-testid="journal-post-back-bottom"
          >
            <ArrowLeft size={13} /> Back to the journal
          </Link>
          {nextPost && nextPost.slug !== slug && (
            <Link
              to={`/journal/${nextPost.slug}`}
              data-testid="journal-post-next"
              className="group text-right md:text-right"
            >
              <p className="overline text-sage mb-2">Next story</p>
              <p className="font-serif italic text-lg md:text-xl text-ink group-hover:text-sage transition-colors inline-flex items-center gap-2">
                {nextPost.title}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </p>
            </Link>
          )}
        </div>
      </section>
    </article>
  );
}
